import {
  INVOICE_EMAIL_STATUSES,
  INVOICE_FLAG_REASONS,
  INVOICE_FLAG_SEVERITIES,
  INVOICE_LINE_KINDS,
  INVOICE_RECIPIENT_KINDS,
  INVOICE_SOURCES,
  INVOICE_STATUSES,
  INVOICE_TYPES,
  type InvoiceFlagFacts,
  MAX_INVOICE_RECIPIENTS,
} from '@clockwise/shared';
import { z } from 'zod';

/**
 * Money crosses the wire as a STRING, never a number.
 *
 * The columns are `numeric(12,2)`; Drizzle surfaces them as decimal strings and
 * they stay that way through the API and into the form state. Parsing to a JS
 * number would reintroduce the binary-float rounding the column type exists to
 * avoid, and `Number('')` silently yields `0` — a missing amount would render
 * as a real $0.00 charge (G15). Same contract as `customer_expenses.cost`.
 */
const money = z.string();

export const InvoiceLineItemSchema = z.object({
  id: z.string(),
  invoiceId: z.string(),
  lineNo: z.number().int(),
  kind: z.enum(INVOICE_LINE_KINDS),
  description: z.string(),
  /** Null on a flat-fee row, which prints an empty Qty/Rate pair. */
  quantity: money.nullable(),
  rate: money.nullable(),
  /** Always present — the record stores what printed, never a recipe. */
  amount: money,
  sourceType: z.string().nullable(),
  sourceId: z.string().nullable(),
  /** Which engagement this line bills, and the SOW version that priced it.
   *  Per LINE because one document can carry lines sold under several
   *  agreements. Both null on hand-typed and expense lines. */
  engagementId: z.string().nullable(),
  sowVersion: z.number().nullable(),
});
export type InvoiceLineItem = z.infer<typeof InvoiceLineItemSchema>;

/**
 * Where part of a line's money is booked — finance-only, never printed. A line
 * with one account has one allocation for its whole amount; a split line has
 * one per account; a line with no account yet has none. Returned beside the
 * lines as `related.allocations`, keyed by `lineItemId`.
 */
export const InvoiceLineAllocationSchema = z.object({
  id: z.string(),
  lineItemId: z.string(),
  invoiceId: z.string(),
  accountId: z.string(),
  position: z.number().int(),
  amount: money,
});
export type InvoiceLineAllocation = z.infer<typeof InvoiceLineAllocationSchema>;

export const InvoiceIssuerSchema = z.object({
  name: z.string(),
  addressLines: z.array(z.string()),
  phone: z.string().optional(),
  fax: z.string().optional(),
  email: z.string().optional(),
  website: z.string().optional(),
});

/** One address the invoice is emailed to. See `InvoiceRecipient` in
 *  `@clockwise/shared`. */
export const InvoiceRecipientSchema = z.object({
  email: z.string(),
  kind: z.enum(INVOICE_RECIPIENT_KINDS),
});

export const InvoiceSchema = z.object({
  id: z.string(),
  invoiceNumber: z.string(),
  invoiceSequence: z.number().int(),
  customerId: z.string(),
  /** Null while a draft has nothing billable on it. Settled on approve. */
  invoiceType: z.enum(INVOICE_TYPES).nullable(),
  status: z.enum(INVOICE_STATUSES),
  source: z.enum(INVOICE_SOURCES),
  invoiceDate: z.string(),
  dueDate: z.string().nullable(),
  terms: z.string().nullable(),
  currency: z.string(),
  /** Snapshot, taken at create time — not a live read of the customer. */
  billToName: z.string(),
  billToAddressLines: z.array(z.string()),
  /** Everyone the invoice is emailed to, To and CC, in order. Delivery only —
   *  never printed. */
  recipients: z.array(InvoiceRecipientSchema),
  /** The day to email it, or null to send by hand. Not content: setting it
   *  never returns an approved invoice to draft. */
  scheduledSendOn: z.string().nullable(),
  /** Emailed as soon as it is approved, at the next sweep. Never set with a
   *  send date. */
  sendOnApproval: z.boolean(),
  /** How many times sending has been tried; the sweeper stops at a cap. */
  sendAttempts: z.number().int(),
  /** Why the last send failed, or null. Cleared on success, edit or reschedule. */
  sendError: z.string().nullable(),
  /** Set while a send is in progress. */
  sendLeaseExpiresAt: z.string().nullable(),

  // ---- Email tracking ----------------------------------------------------
  // What SES reported about the email the invoice went out in. Written by the
  // event webhook; read-only here. See docs/email-tracking.md.
  /** The SES message id of the send. */
  emailMessageId: z.string().nullable(),
  /** The last MEANINGFUL status (an open is not undone by a late delivery);
   *  null when the invoice was never emailed. */
  emailStatus: z.enum(INVOICE_EMAIL_STATUSES).nullable(),
  emailDeliveredAt: z.string().nullable(),
  emailFirstOpenedAt: z.string().nullable(),
  emailLastOpenedAt: z.string().nullable(),
  /** Approximate — some mail apps fetch the tracking image on arrival, others
   *  never fetch it. */
  emailOpenCount: z.number().int(),
  emailBouncedAt: z.string().nullable(),
  /** Who bounced and why, as the mail server said it. */
  emailBounceReason: z.string().nullable(),
  emailComplainedAt: z.string().nullable(),
  billToContactId: z.string().nullable(),
  /** Snapshot of `invoice_settings` at create time, for the same reason. */
  issuer: InvoiceIssuerSchema,
  paymentDetails: z.array(z.string()),
  /** Sum of the line items, maintained server-side in the same transaction as
   *  any line write. Also what the invoice is owed for: nothing is deducted
   *  from it, so there is no separate balance-due figure. */
  total: money,
  notes: z.string().nullable(),
  /** This invoice's own email message, as finished text — set only when
   *  someone edited it. Null sends the invoice-settings template, filled in. */
  emailBody: z.string().nullable(),
  approvedAt: z.string().nullable(),
  approvedBy: z.string().nullable(),
  sentAt: z.string().nullable(),
  syncedToErp: z.boolean(),
  syncedToErpAt: z.string().nullable(),
  erpReference: z.string().nullable(),
  /** The service period this invoice covers, half-open `[start, end)`. A
   *  timesheet entry or a customer expense falls inside this range or it does
   *  not, and the label printed on the document is derived from it
   *  (`deriveServicePeriodLabel`). Null on an ad-hoc invoice that covers no
   *  period. */
  servicePeriodStart: z.string().nullable(),
  servicePeriodEnd: z.string().nullable(),

  /** The ENGAGEMENT this invoice bills, when it bills exactly one. NULL means
   *  the document gathers several — a customer's retainers on one bill, or a
   *  consolidated invoice carrying retainer and T&M together — which is the
   *  common case. Only an engagement with `consolidate: false` stands alone. */
  engagementId: z.string().nullable(),

  // ---- Review ------------------------------------------------------------
  /** Which version of the SOW priced this invoice. Null for a hand-typed one,
   *  for everything raised before review shipped, and for a document gathering
   *  engagements from several SOWs. */
  sowVersion: z.number().nullable(),
  /** The invoice this one was compared against — the same agreement's most
   *  recent earlier service period. */
  priorInvoiceId: z.string().nullable(),
  /**
   * What that invoice billed, copied down at review time.
   *
   * Null means there was NO prior invoice, which is not the same as a prior of
   * zero: the list renders "No prior" rather than `$0.00`, since a zero there
   * reads as a real amount and would imply a 100% swing (G15).
   */
  priorTotal: money.nullable(),
  /** How many review flags this invoice carries. The Flagged filter is
   *  `reviewFlagCount gt 0`; the count itself is worth showing ("2 flags").
   *  Reasons are fetched per invoice from `GET /invoices/:id/flags`. */
  reviewFlagCount: z.number(),

  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Invoice = z.infer<typeof InvoiceSchema>;

/**
 * One review flag.
 *
 * `facts` is a discriminated union keyed on `reason` — see `InvoiceFlagFacts`
 * in `@clockwise/shared`, and `describeInvoiceFlag` there for the ONE function
 * that turns it into a title and a detail line. Render through that rather
 * than composing prose here, or the list popover, the invoice panel and the
 * exports will describe the same flag three different ways.
 */
export const InvoiceFlagSchema = z.object({
  id: z.string(),
  invoiceId: z.string(),
  reason: z.enum(INVOICE_FLAG_REASONS),
  severity: z.enum(INVOICE_FLAG_SEVERITIES),
  facts: z.custom<InvoiceFlagFacts>(),
  raisedAt: z.string(),
});
export type InvoiceFlag = z.infer<typeof InvoiceFlagSchema>;

/** One line on the way in. `amount` may be omitted when `quantity` and `rate`
 *  are both present — the server derives and stores it explicitly. */
export const InvoiceLineItemInput = z.object({
  kind: z.enum(INVOICE_LINE_KINDS).optional(),
  description: z.string().trim().min(1),
  quantity: z.union([z.string(), z.number()]).nullable().optional(),
  rate: z.union([z.string(), z.number()]).nullable().optional(),
  amount: z.union([z.string(), z.number()]).nullable().optional(),
  sourceType: z.string().nullable().optional(),
  sourceId: z.string().nullable().optional(),
  /** Attribute a line to an agreement. Settable, unlike the invoice-level
   *  `engagementId`, which is half of the generation uniqueness key. */
  engagementId: z.string().nullable().optional(),
  sowVersion: z.number().nullable().optional(),
  /** Where the line's money is booked. Absent/empty: no account yet (a draft
   *  may be saved so; approval asks for one). One entry: the whole line —
   *  its amount may be omitted. Several: a split, every part stated, each
   *  account once. */
  allocations: z
    .array(z.object({ accountId: z.string(), amount: z.string().nullable().optional() }))
    .optional(),
});
export type InvoiceLineItemInput = z.infer<typeof InvoiceLineItemInput>;

/** Bill-to overrides. Anything omitted is copied from the customer and its
 *  billing contact — this is how a one-off address is invoiced without
 *  editing the customer record. */
export const InvoiceBillToInput = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  addressLines: z.array(z.string().trim().min(1).max(120)).max(6).optional(),
  /** Everyone the invoice is emailed to; replaces the whole list. Omitted, it
   *  follows the contact: kept on patch, or taken from a newly named contact. */
  recipients: z
    .array(
      z.object({
        email: z.string().trim().email().max(160),
        kind: z.enum(INVOICE_RECIPIENT_KINDS),
      }),
    )
    .max(MAX_INVOICE_RECIPIENTS)
    .optional(),
  contactId: z.string().nullable().optional(),
});

/** `invoiceNumber` is deliberately absent from create: the server allocates
 *  it. A draft can be renumbered afterwards through patch. */
/**
 * The bill-to block an invoice for this customer would snapshot right now.
 *
 * `contactId` is null and `recipients` empty when the customer has no billing
 * contact — a real state the create screen names rather than hides.
 */
export const InvoiceBillToPreviewSchema = z.object({
  name: z.string(),
  addressLines: z.array(z.string()),
  recipients: z.array(InvoiceRecipientSchema),
  contactId: z.string().nullable(),
});
export type InvoiceBillToPreview = z.infer<typeof InvoiceBillToPreviewSchema>;

/**
 * The tenant-level half of what the create screen needs to draw an invoice
 * before it exists. No customer required — see `NewInvoiceBillTo` for the
 * customer-specific half.
 *
 * `invoiceNumberPreview` is a preview, NOT a reservation — nothing is
 * allocated until the invoice is saved, so two people on this screen at once
 * see the same number and the second to save gets the next one. Render it as
 * expected, never as settled.
 */
export const NewInvoiceDefaultsSchema = z.object({
  issuer: InvoiceIssuerSchema,
  paymentDetails: z.array(z.string()),
  currency: z.string(),
  terms: z.string().nullable(),
  invoiceNumberPreview: z.string().nullable(),
  /** The template a new invoice's email is filled from unless edited. */
  emailBodyTemplate: z.string(),
  /** The subject line it is sent under. */
  emailSubjectTemplate: z.string(),
});
export type NewInvoiceDefaults = z.infer<typeof NewInvoiceDefaultsSchema>;

/**
 * The Bill To block a new invoice for this customer would snapshot right
 * now — the customer-specific half of the create screen's preview.
 */
export const NewInvoiceBillToSchema = z.object({
  billTo: InvoiceBillToPreviewSchema,
  /** The customer's stored billing address, as held — for rebuilding the
   *  Bill To block when a different contact is picked. */
  billingAddress: z.unknown(),
  /** The customer's credit term as invoice Terms; null when it has none, and
   *  the tenant default (`NewInvoiceDefaults.terms`) applies. */
  terms: z.string().nullable(),
});
export type NewInvoiceBillTo = z.infer<typeof NewInvoiceBillToSchema>;

export const CreateInvoiceInput = z.object({
  customerId: z.string().uuid(),
  invoiceDate: z.string(),
  dueDate: z.string().nullable().optional(),
  terms: z.string().max(60).nullable().optional(),
  /** Both ends together or neither — a half-specified range is uncomparable,
   *  which defeats the only reason to store it. Half-open, so an end equal to
   *  the start covers nothing. */
  servicePeriodStart: z.string().nullable().optional(),
  servicePeriodEnd: z.string().nullable().optional(),
  scheduledSendOn: z.string().nullable().optional(),
  /** Omitted on create, defaults to true unless a send date is given. */
  sendOnApproval: z.boolean().optional(),
  currency: z.string().length(3).optional(),
  billTo: InvoiceBillToInput.optional(),
  notes: z.string().max(4000).nullable().optional(),
  /** Finished text, stored only when it differs from what the template
   *  renders for this invoice. Null resets to the template. */
  emailBody: z.string().max(5000).nullable().optional(),
  /** May be empty — a draft with no lines is a real state. "At least one
   *  line" is enforced on approve, when it becomes a document. */
  lineItems: z.array(InvoiceLineItemInput).optional(),
});
export type CreateInvoiceInput = z.infer<typeof CreateInvoiceInput>;

/** `customerId` is absent on purpose: re-pointing an invoice at a different
 *  customer would leave the bill-to snapshot describing someone else.
 *  `lineItems`, when sent, REPLACES the whole collection. */
export const PatchInvoiceInput = CreateInvoiceInput.omit({
  customerId: true,
  invoiceDate: true,
})
  .partial()
  .extend({
    invoiceDate: z.string().optional(),
    /** Renumber a draft. Refused (400 on `invoiceNumber`) when taken. */
    invoiceNumber: z.string().trim().min(1).max(64).optional(),
    syncedToErp: z.boolean().optional(),
    flagged: z.boolean().optional(),
    erpReference: z.string().max(120).nullable().optional(),
  });
export type PatchInvoiceInput = z.infer<typeof PatchInvoiceInput>;

/** Per-row outcome of a bulk transition. The batch does not abort on the first
 *  bad row — it reports which ones it skipped and why. */
export const BulkInvoiceResultSchema = z.object({
  succeeded: z.array(z.string()),
  failed: z.array(
    z.object({
      id: z.string(),
      reason: z.enum([
        'NOT_FOUND',
        'FORBIDDEN',
        'INVALID_TRANSITION',
        'NO_LINE_ITEMS',
        'NOT_CLASSIFIED',
        // Approval only: a line has no account, or a split does not add up.
        'ACCOUNTS_INCOMPLETE',
        'NO_RECIPIENT',
        // Rescheduling only: the date falls before that invoice's own date.
        'SEND_BEFORE_INVOICE_DATE',
        // Approval only: flagged "Require manual review", so it is approved
        // one at a time, never by filter or multi-select.
        'MANUAL_REVIEW_REQUIRED',
        // Not a judgement about the row: the run stopped before reaching it.
        // The invoice is unchanged and retrying is the whole remedy.
        'INTERRUPTED',
      ]),
      message: z.string(),
    }),
  ),
});
export type BulkInvoiceResult = z.infer<typeof BulkInvoiceResultSchema>;

/**
 * How many matching invoices sit in each status. Every status is present, so a
 * caller never has to tell "none" apart from "not counted".
 */
export type InvoiceStatusCounts = Record<(typeof INVOICE_STATUSES)[number], number>;

/**
 * What a bulk transition acts on.
 *
 * `ids` is "these ones, the ones I ticked". `filter` is "everything matching
 * what I am looking at" — resolved to rows on the server at the moment of the
 * write, so it covers the pages the user never opened and cannot go stale
 * between reading and clicking. `excludeIds` drops the handful they unticked
 * after selecting the set.
 */
export type InvoiceBulkTarget =
  | { ids: readonly string[] }
  | { filter: InvoiceBulkFilter; excludeIds?: readonly string[] };

/**
 * What approve acts on. `reviewed: true` is sent ONLY by the invoice editor,
 * with its one id: it is how an invoice billing a manual-review engagement
 * gets approved. Every other approve skips those with `MANUAL_REVIEW_REQUIRED`.
 */
export type InvoiceApproveTarget = InvoiceBulkTarget | { ids: readonly [string]; reviewed: true };

/** How invoices get emailed: as soon as they are approved, on a date, or only
 *  when someone presses Send. `scheduledSendOn` goes with `date`, and only
 *  with it. */
export type InvoiceScheduleSend =
  | { sendMode: 'approval' | 'manual' }
  | { sendMode: 'date'; scheduledSendOn: string };

/** The list filter as a JSON body: real arrays and booleans, not the URL's
 *  comma-joined strings. Ordering and paging are absent — neither says
 *  anything about which invoices match. */
export type InvoiceBulkFilter = Omit<
  InvoiceListQuery,
  'sortField' | 'sortDirection' | 'limit' | 'offset'
>;

/** The list page's filters, flat-encoded so the URL is bookmarkable. */
export interface InvoiceListQuery {
  q?: string;
  /** Any-of. Empty or absent means every customer the caller can reach —
   *  which is still bounded by their own customer scope, server-side. */
  customerIds?: string[];
  status?: (typeof INVOICE_STATUSES)[number];
  invoiceType?: (typeof INVOICE_TYPES)[number];
  source?: (typeof INVOICE_SOURCES)[number];
  syncedToErp?: boolean;
  /** Whether the invoice carries review flags. Absent means no opinion. */
  flagged?: boolean;
  /** Only invoices raised by this generation run. */
  generationRunId?: string;
  /** Inclusive on both ends — what a human means by "June". */
  dateFrom?: string;
  dateTo?: string;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

/** Per-row outcome of an invoice import. Every source row of a failed invoice
 *  is reported, not just the first — the operator has to find them all in the
 *  spreadsheet. `updated` is always 0: import creates and never overwrites. */
export interface ImportInvoicesRow {
  rowNumber: number;
  outcome: 'created' | 'updated' | 'unchanged' | 'failed';
  errors?: string[];
}

export interface ImportInvoicesResult {
  totalRows: number;
  created: number;
  updated: number;
  failed: number;
  rows: ImportInvoicesRow[];
}

// ---- Generation runs ------------------------------------------------------

/** Why a customer on a run produced no invoice. Mirrors `SkipReason` in the
 *  API; a union rather than `string` so the UI's reason-to-copy map is
 *  exhaustive and a new reason is a type error rather than a blank cell. */
export type InvoiceSkipReason =
  | 'CADENCE_NOT_DUE'
  | 'NO_RETAINER_SOW'
  | 'NO_PRICE_FOR_PERIOD'
  | 'ALREADY_GENERATED'
  | 'ALREADY_INVOICED'
  | 'INVOICING_PAUSED'
  | 'OUT_OF_SCOPE'
  /** The engagement's own billing window does not cover the period — it has
   *  either not started billing yet, or has stopped. */
  | 'OUTSIDE_BILLING_WINDOW'
  /** A one-time SOW line: storable and selectable, but with no recurrence
   *  there is no period yet. Scheduled by the forthcoming billing schedule. */
  | 'AWAITING_BILLING_SCHEDULE'
  /** The generation run was abandoned before it reached this engagement.
   *  Distinct from `FAILED`: nothing about the engagement is wrong, the batch
   *  stopped — so the action is to generate again, not to fix a SOW. */
  | 'RUN_ABANDONED'
  /** The customer consolidates, so this engagement's fee rides on the ONE
   *  arrears document the retainer run raises for them at period end. */
  | 'CONSOLIDATED_AT_PERIOD_END'
  /** The mirror, on the T&M side: the retainer run raises one document
   *  carrying both the fees and these hours, so this run stands down. */
  | 'CONSOLIDATED_WITH_RETAINER'
  // ---- Time & materials ----------------------------------------------------
  // These existed in the API's `SkipReason` from the day the T&M engine
  // shipped but never reached this union, so `skipReasonCopy` — a switch with
  // no default, exhaustive over what it CAN see — returned `undefined` for
  // them. The run detail page tolerated that; the "not invoiced" panel read
  // `.label` off it and threw, for any month with a T&M skip.
  /** The engagement sells hours but prices no service role. */
  | 'NO_TM_RATES'
  /** No billable hours were logged for the period at all. Benign — a quiet
   *  month is normal, and filtered out of "who was not invoiced". */
  | 'NO_BILLABLE_HOURS'
  /** Billable hours WERE logged and none are approved. The actionable half of
   *  the pair above, and the only one of the two that is a problem. */
  | 'HOURS_NOT_APPROVED'
  /** Approved billable hours exist but no engagement is attributed, so no
   *  invoice bills them. Actionable. */
  | 'HOURS_UNATTRIBUTED'
  /** The customer's other concurrent engagement bills these hours. */
  | 'HOURS_CLAIMED_BY_ANOTHER_SOW'
  | 'FAILED';

export type InvoiceRunStatus =
  | 'queued'
  | 'running'
  | 'completed'
  | 'completed_with_errors'
  | 'abandoned';

export type InvoiceRunItemStatus = 'pending' | 'processing' | 'created' | 'skipped' | 'failed';

/** What to generate. Both filters are AND-ed, and OMITTING one means "every
 *  supported value" — not "none". */
export interface GenerationRunInput {
  /** The month being INVOICED, not the close date. Any day in it. */
  invoiceMonth: string;
  /**
   * Which engine to run. Omitted means `retainer`.
   *
   * `tm` bills a month of approved, billable hours at the SOW's agreed rates —
   * always in arrears, always the month named here, whatever the tenant's
   * advance/arrears setting says. The two filters below apply to a retainer
   * run only: a T&M line carries no frequency for one to match.
   */
  kind?: 'retainer' | 'tm';
  /** The SOW's vocabulary, not the customer's — generation is driven by SOWs.
   *  Omitting one means "every supported value", not "none". */
  sowLineTypes?: string[];
  sowFrequencies?: string[];
}

export interface InvoiceRunPreview {
  invoiceMonth: string;
  runDate: string;
  /** The date every invoice in the run carries — the period START for a
   *  retainer, the period's last day for T&M — or null when the run's periods
   *  are dated differently, in which case each period carries its own. */
  invoiceDate: string | null;
  /** One entry per distinct period. Render the period's own `label` — in
   *  arrears a multi-month period ENDS with the picked month rather than
   *  starting there, so "March 2026" would be wrong for a quarterly customer
   *  whose invoice will print "Jan–Mar 2026". */
  periods: Array<{
    label: string;
    start: string;
    end: string;
    invoiceDate: string;
    customerCount: number;
  }>;
  eligible: number;
  alreadyInvoiced: number;
  excluded: Array<{ reason: InvoiceSkipReason; count: number }>;
  /**
   * Variable expense lines the run will leave OFF, because no amount was
   * recorded for them in the period.
   *
   * Computed over exactly the candidates this run would bill, using the same
   * predicate the `MISSING_VARIABLE_EXPENSE` flag uses — so this number and
   * the flags on the resulting drafts cannot disagree.
   *
   * `count: 0` is the healthy case, and is what lets the generate dialog stay
   * a single screen instead of growing a step nobody would read.
   */
  missingVariableExpenses: {
    count: number;
    /** A few to name, so the warning points at something concrete. The full
     *  list lives on the variable-expenses grid. */
    sample: Array<{ customerName: string; description: string; itemName: string }>;
  };
}

export interface InvoiceGenerationRun {
  id: string;
  /** Which engine drove the run. `retainer` on every run recorded before T&M
   *  generation existed — there was only one engine then. */
  runKind: 'retainer' | 'tm';
  trigger: 'on_demand' | 'scheduled';
  requestedBy: string | null;
  invoiceMonth: string;
  runDate: string;
  customerTypes: string[];
  invoiceCadences: string[];
  status: InvoiceRunStatus;
  totalItems: number;
  createdCount: number;
  skippedCount: number;
  failedCount: number;
  notDueCount: number;
  pausedCount: number;
  filteredCount: number;
  createdTotal: string;
  /** How many of this run's invoices carry review flags. Derived per request,
   *  not stored — an invoice edited after the run can gain or lose flags. */
  flaggedCount?: number;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InvoiceGenerationRunItem {
  id: string;
  runId: string;
  customerId: string;
  customerName: string;
  /** The engagement this row bills, when the document bills exactly one —
   *  i.e. when that engagement opted out of grouping. NULL is the common
   *  case: the document gathers the customer's engagements, and with
   *  `customerId` this forms the item's SUBJECT, which is what one row
   *  identifies. */
  engagementId: string | null;
  /** The agreement this row is about, when every engagement on the document
   *  comes from the same one. NULL once it spans two. */
  sowId: string | null;
  sowName: string;
  /** Whether this document gathers the month's hours alongside the retainers,
   *  and therefore bills in arrears. */
  consolidated: boolean;
  customerType: string | null;
  invoiceCadence: string | null;
  invoiceMonth: string;
  invoicePeriodStart: string;
  invoicePeriodEnd: string;
  status: InvoiceRunItemStatus;
  skipReason: InvoiceSkipReason | null;
  invoiceId: string | null;
  attempts: number;
  lastError: string | null;
  updatedAt: string;
}

/** The cheap poll behind the progress toast — counts only. */
export interface InvoiceRunProgress {
  id: string;
  status: InvoiceRunStatus;
  total: number;
  done: number;
  created: number;
  skipped: number;
  failed: number;
}

/** A document with no invoice for a month, and why. `systemError` means no
 *  user action can fix it — render those without a call to action.
 *
 *  One row per (SUBJECT, span), not per customer: a customer whose opted-out
 *  engagement could not be priced has a real problem even though their
 *  gathered invoice went out fine — and one customer can owe two periods in
 *  the same month, which is two rows. */
export interface UnbilledCustomer {
  customerId: string;
  customerName: string;
  /** The engagement that was not invoiced, when the document billed exactly
   *  one. NULL once it gathered the customer's — the row is then about the
   *  customer, which is the honest answer: nothing of theirs billed. */
  engagementId: string | null;
  /** The agreement, when the document named one. NULL once it spans two. */
  sowId: string | null;
  sowName: string;
  /** The span that was not invoiced. The subject alone does not identify a
   *  row. */
  periodStart: string;
  periodEnd: string;
  invoiceMonth: string;
  reason: InvoiceSkipReason;
  detail: string | null;
  systemError: boolean;
  /** When a run last tried to bill this (SOW, span), ISO-8601 over the wire.
   *  The daily report uses it to nudge about a row only while the run that
   *  found it is recent; the panel shows every row regardless. */
  lastAttemptedAt: string;
}

export interface InvoiceRunListQuery {
  month?: string;
  status?: InvoiceRunStatus;
  /** Narrow the history to one engine's runs. */
  kind?: 'retainer' | 'tm';
  limit?: number;
  offset?: number;
}

export interface InvoiceRunItemsQuery {
  status?: InvoiceRunItemStatus;
  limit?: number;
  offset?: number;
}

/**
 * A file attached to an invoice. Delivery material, not part of the printed
 * document; addable and removable until the invoice is sent. Where the bytes
 * live is deliberately not exposed — fetch them with `downloadAttachment`.
 */
export const InvoiceAttachmentSchema = z.object({
  id: z.string().uuid(),
  invoiceId: z.string().uuid(),
  filename: z.string(),
  contentType: z.string(),
  sizeBytes: z.number().int(),
  uploadedBy: z.string().nullable(),
  createdAt: z.string(),
});
export type InvoiceAttachment = z.infer<typeof InvoiceAttachmentSchema>;

/**
 * Where an invoice emailed now would actually land. While `redirectToSender`
 * is true, neither the invoice's contacts nor any address typed for the send
 * receives it — `redirectTo` does — so the Send dialog can say so.
 */
export interface InvoiceSendConfig {
  redirectToSender: boolean;
  redirectTo: string | null;
}

/**
 * Who one invoice is emailed to: its own contacts, additional addresses, or
 * both (never neither). Additional addresses go on the To line for this send
 * only and are never stored.
 */
export interface InvoiceSendChoice {
  includeContacts: boolean;
  additionalTo: string[];
}

/** What actually happened to a sent email. */
export interface InvoiceDeliveryReceipt {
  /** Every address on the email, To then CC. */
  deliveredTo: string[];
  /** True when the redirect guard sent it to the sender instead. */
  redirected: boolean;
  /** True when this send moved the invoice from approved to sent. */
  markedSent: boolean;
}

export type InvoiceSendSkipReason =
  | 'NOT_FOUND'
  | 'FORBIDDEN'
  | 'NOT_APPROVED'
  | 'NO_RECIPIENT'
  | 'ALREADY_SENDING';

/** A bulk send's preview, or what it queued. */
export interface InvoiceBulkSendResult {
  /** Each to its own contacts. `approved` is a first send; `sent` a resend. */
  send: Array<{
    id: string;
    invoiceNumber: string;
    customerName: string;
    status: 'approved' | 'sent';
    to: string[];
    ccCount: number;
  }>;
  skipped: Array<{
    id: string;
    invoiceNumber: string | null;
    customerName: string | null;
    reason: InvoiceSendSkipReason;
    message: string;
  }>;
}

/** What a queued bulk send reports: how many were queued, and what was
 *  skipped and why. */
export interface InvoiceBulkSendQueued {
  queued: number;
  skipped: InvoiceBulkSendResult['skipped'];
}
